// components/FilePreviewModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';

export default function ModalFilePreview({ show, base64, filename, onHide }) {
  var [error, setError] = useState(null);

  // -------- Inferência pelo filename (extensão) ----------
  function getExtFromFilename(name) {
    if (!name) return '';
    var idx = name.lastIndexOf('.');
    return idx >= 0 ? name.substring(idx + 1).toLowerCase() : '';
  }

  function getMimeFromExt(ext) {
    // mapeamento básico (adicione mais conforme necessidade)
    var map = {
      'pdf': 'application/pdf',
      'csv': 'text/csv',
      'txt': 'text/plain',
      'json': 'application/json',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'webp': 'image/webp',
      // xlsx/docx/pptx não são pré-visualizáveis no navegador sem libs externas
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls':  'application/vnd.ms-excel',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'doc':  'application/msword',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'ppt':  'application/vnd.ms-powerpoint'
    };
    return map[ext] || 'application/octet-stream';
  }

  var name = filename || 'arquivo';
  var ext = getExtFromFilename(name);
  var effectiveType = getMimeFromExt(ext);

  // -------- Utils base64 -> Blob / Download ----------
  function base64ToBlob(b64, type) {
    type = type || 'application/octet-stream';
    var byteChars = atob(b64);
    var byteArrays = [];
    var sliceSize = 1024;

    for (var offset = 0; offset < byteChars.length; offset += sliceSize) {
      var slice = byteChars.slice(offset, offset + sliceSize);
      var byteNumbers = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: type });
  }

  function downloadBase64File(params) {
    params = params || {};
    var b64 = params.base64;
    if (!b64) return;

    var type = params.contentType || 'application/octet-stream';
    var fname = params.filename || 'arquivo';

    var blob = base64ToBlob(b64, type);
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // -------- URL para preview quando aplicável ----------
  var objectUrl = useMemo(function () {
    try {
      if (!base64) return null;
      var blob = base64ToBlob(base64, effectiveType);
      return URL.createObjectURL(blob);
    } catch (e) {
      setError((e && e.message) ? e.message : 'Falha ao gerar preview');
      return null;
    }
  }, [base64, effectiveType]);

  useEffect(function () {
    return function cleanup() {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  // -------- Regras de preview baseadas na EXTENSÃO ----------
  var isImage = ['png','jpg','jpeg','gif','webp'].indexOf(ext) >= 0;
  var isPdf   = ext === 'pdf';
  var isText  = ['csv','txt','json'].indexOf(ext) >= 0;

  // Para preview de texto (csv/txt/json), decodifica base64 em string simples
  function decodeTextFromBase64(b64) {
    try { return atob(b64); } catch (e) { return '(Conteúdo de texto indisponível para preview)'; }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!base64 && !error && (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="mr-2" />
            <span>Carregando…</span>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {base64 && !error && (
          <div>
            {isImage && objectUrl && (
              <img src={objectUrl} alt={name} style={{ maxWidth: '100%' }} />
            )}

            {isPdf && objectUrl && (
              <iframe title="preview-pdf" src={objectUrl} style={{ width: '100%', height: 520, border: 0 }} />
            )}

            {isText && (
              <pre style={{ maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {decodeTextFromBase64(base64)}
              </pre>
            )}

            {!isImage && !isPdf && !isText && (
              <small>
                Arquivo “.{ext}” não é pré‑visualizável no navegador. Use o botão abaixo para baixar.
              </small>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fechar</Button>
        <Button
          variant="primary"
          onClick={function () {
            downloadBase64File({ base64: base64, contentType: effectiveType, filename: name });
          }}
          disabled={!base64}
        >
          Baixar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
